<template>
  <Modal v-model="isDisplayed" v-if="model && team" size="lg" :footer="false" :backdrop="true">
    <template #header>
      <h1 class="text-center text-wrap modal-header" data-test="team-invite-modal-title">
        Invite team members to <br />'{{ team.name }}'
      </h1>
      <button
        class="btn btn-close-sm btn-position"
        @click="closeModal(false)"
        type="button"
      ></button>
    </template>
    <div class="invite-control-container">
      <TeamInviteControl
        v-if="!isLicenceHolder || canInvite"
        :model="model"
        @invite-action="markLevelAsCompletable"
        @previous-invites="markLevelAsCompletable"
        @on-deleted-event="loadInviteModel"
      />
    </div>
    <p>
      You can invite more team members at any time from the
      <a :href="`/team/members/${team.id}`">Team Members</a> page.
    </p>
    <div class="row" style="margin-top: 20px">
      <div class="col-sm-12 text-center modal-button-container">
        <button
          class="btn btn-default-close"
          type="button"
          @click="closeModal(false)"
          data-test="team-invite-modal-close-button"
        >
          Close
        </button>
      </div>
      <p v-if="errorMessage" class="text-danger text-center" data-test="team-invite-modal-error">
        {{ errorMessage }}
      </p>
    </div>

    <!-- NO PERMISSIONS UI -->
    <div v-if="isLicenceHolder && !canInvite" class="col-sm-10 col-sm-offset-1 text-big">
      <p>You do not have permission to invite to this team</p>
    </div>
    <!-- END OF NO PERMISSIONS UI -->
  </Modal>
</template>

<script setup lang="ts">
import { Modal } from "uiv";
import { computed, inject, onMounted, Ref, ref } from "vue";
import axios from "axios";
import { debug } from "debug";
import {
  ChargebeeSubscriptionApiModel,
  InviteControlApiModel,
  TeamApiModel,
  TeamSettingsApiModel,
} from "../../api-models";
import { GroupInviteModel } from "../../views/team/team-invite.vue";
import TeamInviteControl from "./team-invite-control.vue";
import { csContext } from "../../services/cs-context";

const team = inject("teamModel") as Ref<TeamApiModel>;
const settings = inject("teamSettingsModel") as Ref<TeamSettingsApiModel>;

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits(["close-modal"]);

const isDisplayed = computed(() => {
  return props.modelValue;
});

const isLoading = ref(true);
const errorMessage = ref<string | null>(null);

const model = ref<InviteControlApiModel | null>(null);
const subscriptionModel = ref<ChargebeeSubscriptionApiModel | null>(null);
const seats = ref<{ totalSeats: number; seatsInUse: number; availableSeats: number } | null>(null);

const canInvite = computed(() => {
  return settings.value.userPermissions.canInviteToGroup;
});

const isLicenceHolder = computed(() => {
  return csContext.userProfile.hasLicence;
});

function closeModal(triggerRefresh: boolean) {
  emit("close-modal", triggerRefresh);
}

const isLevelCompletable = ref(false);

function markLevelAsCompletable() {
  if (!isLevelCompletable.value) {
    isLevelCompletable.value = true;
  }
}

async function loadSubscription(): Promise<void> {
  return axios.get("/api/subscription/default").then((resp) => {
    const subModel = resp.data as ChargebeeSubscriptionApiModel | null;
    if (subModel) {
      subscriptionModel.value = subModel;
      seats.value = {
        totalSeats: subModel.currentPaidLicenceCount,
        seatsInUse: subModel.licences.length,
        availableSeats: subModel.currentPaidLicenceCount - subModel.licences.length,
      };
    }
  });
}

async function loadInviteModel(): Promise<void> {
  return axios.get(`/team/invitemodel/${team.value.id}`).then((resp) => {
    const inviteModel = resp.data as GroupInviteModel;
    const controlModel = inviteModel.invites;
    controlModel.sendUrl = `/invitecodes/sendgroupinvites/${team.value.id}`;
    controlModel.peoplePhrase = inviteModel.groupName;
    debug(inviteModel.groupName);
    model.value = controlModel;
  });
}

function loadData() {
  const axiosPromises: Promise<void>[] = [];
  axiosPromises.push(loadInviteModel());
  axiosPromises.push(loadSubscription());

  Promise.all(axiosPromises).then(() => {
    isLoading.value = false;
  });
}

onMounted(() => {
  loadData();
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.org-admin-list {
  padding: 20px 0;

  a {
    font-weight: bold;
  }
}

h3 {
  text-align: center;
}

.text-wrap {
  overflow-wrap: break-word;
}

.no-seats {
  text-align: center;

  h3 {
    color: @dark-background-color;
  }
}

hr {
  border-top: 2px solid @charismatic-color;
  margin-top: 5px;
}

.team-invite-image {
  position: relative;
  left: 10%;
  width: 70%;
}

@media screen and (max-width: 840px) {
  hr {
    margin-left: 0;
    margin-right: 0;
  }
}

.modal-button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
}

.modal-header {
  margin: 0;
  border-bottom: none;

  .btn-position {
    position: absolute;
    top: 15px;
    right: 15px;
  }
}

.invite-control-container {
  width: 100%;
  margin: auto;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;

  .row {
    margin: 0;
  }

  @media (max-width: @screen-xs-max) {
    max-height: 400px;
    overflow-x: auto;
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
}
</style>
<style lang="less">
.invite-control {
  h3 {
    margin-top: 0;
  }
}

.invite-control-container {
  hr {
    margin: 5px 0 10px;
  }

  .invite-control {
    .col-sm-8.col-sm-offset-1,
    .col-sm-12.col-sm-offset-1,
    .col-sm-10.col-sm-offset-1 {
      margin-left: 0;
    }
  }
}
</style>
